import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { APP_ROUTES } from "@/config/routes.config";
import useAuthUser from "@/hooks/useAuthUser";
import { cn } from "@/lib/utils";
import { PATH_LOGOUT, PATH_SETTINGS_PROFILE } from "@/routes/paths";
import { useRolesStore } from "@/store/roles/roles.store";
import {
  ArrowUpRight,
  ChevronDown,
  Command as CommandIcon,
  DollarSign,
  LogOut,
  Menu as MobileMenuIcon,
  Plus,
  Search,
  Sparkles,
  User as UserIcon,
  UserPlus,
  Workflow,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddDealSidebar from "@/pages/deals/components/add-deal-sidebar";
import { CreatePipelineModal } from "@/pages/deals/components/deals-toolbar/create.pipeline.modal";
import AddLeadModal from "@/pages/leads/components/add-lead/add.lead.modal";
import GlobalSearch, { SearchPage } from "./global.search";

/**
 * Main desktop top bar, ported from demo-ui Topbar. Scoped to real
 * functionality: the search button opens the leads/pages palette (existing
 * GET /lead?query=), Quick create links to the route-addressable create
 * flows, and the user chip shows the real /me identity. The demo's
 * notifications bell is omitted — the app has no notification inbox.
 */

// Demo-ui Quick create actions — each opens the corresponding real creation
// flow (New Lead modal over POST /lead; the existing AddDealSidebar and
// CreatePipelineModal mounted here, page-independent).
const QUICK_ACTIONS = [
  {
    id: "lead" as const,
    label: "New Lead",
    hint: "Capture a prospect manually",
    icon: UserPlus,
    iconBg: "bg-brand-500/10",
    iconRing: "ring-brand-400/25",
    iconColor: "text-brand-300",
  },
  {
    id: "deal" as const,
    label: "New Deal",
    hint: "Add a deal into any pipeline stage",
    icon: DollarSign,
    iconBg: "bg-accent-green/10",
    iconRing: "ring-accent-green/25",
    iconColor: "text-accent-green",
  },
  {
    id: "pipeline" as const,
    label: "New Pipeline",
    hint: "Spin up a fresh kanban board",
    icon: Workflow,
    iconBg: "bg-accent-purple/10",
    iconRing: "ring-accent-purple/25",
    iconColor: "text-accent-purple",
  },
];

type QuickActionId = (typeof QUICK_ACTIONS)[number]["id"];

const UserAvatar = ({
  name,
  url,
  size,
}: {
  name: string;
  url?: string | null;
  size: number;
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!url && !imgFailed;
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  return (
    <div
      className="relative grid shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 font-semibold text-white ring-1 ring-white/10"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      title={name}
    >
      {showImage ? (
        <img src={url as string} alt={name} className="h-full w-full object-cover" onError={() => setImgFailed(true)} />
      ) : (
        <span>{initials || "?"}</span>
      )}
    </div>
  );
};

const MainTopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, toggleSidebar } = useSidebar();
  const { user, hasPermission } = useAuthUser();
  const roles = useRolesStore((s) => s.roles);

  const [searchOpen, setSearchOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState<QuickActionId | null>(null);

  const handleQuickAction = (id: QuickActionId) => {
    setQuickOpen(false);
    setCreateOpen(id);
  };

  useEffect(() => {
    useRolesStore.getState().getAllRoles();
  }, []);

  // ⌘K / Ctrl+K toggles the palette (demo AppLayout behavior)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Route change closes any open overlay
  useEffect(() => {
    setSearchOpen(false);
    setQuickOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : "";
  const roleName = user?.isAdmin ? "Admin" : roles.find((r) => user?.roles?.includes(r.id))?.name;

  // Only the sidebar's main pages (Dashboard, Funnels, Leads, Pipelines,
  // Call Center, Meeting Scheduler), with the same visibility rule the
  // sidebar uses — the palette only offers pages the user can actually open.
  const searchPages: SearchPage[] = APP_ROUTES.filter((route) => {
    if (!route.title || route.section !== "main") return false;
    if (route.public || user?.isAdmin) return true;
    return route.requiredPermissions?.length ? hasPermission(route.requiredPermissions) : false;
  }).map((route) => ({ path: route.path, title: route.title as string, icon: route.icon }));

  const panelClass =
    "overflow-hidden rounded-2xl border-white/[0.08] bg-gradient-to-b from-ink-800/95 to-ink-900/95 p-0 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl";

  return (
    <>
      <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-2 border-b border-white/[0.04] bg-[rgba(8,10,22,0.55)] px-4 backdrop-blur-xl sm:gap-3 sm:px-6">
        {isMobile && (
          <button type="button" onClick={toggleSidebar} className="icon-btn md:hidden" aria-label="Open menu">
            <MobileMenuIcon className="h-4 w-4" />
          </button>
        )}

        {/* Search trigger */}
        <div className="flex max-w-xl flex-1 items-center">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="group relative flex h-10 w-full items-center rounded-xl border border-white/[0.06] bg-white/[0.02] pl-3 pr-2 text-left transition hover:border-white/[0.14] hover:bg-white/[0.04]"
          >
            <Search className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-white" />
            <span className="ml-2.5 flex-1 truncate text-sm text-slate-500 group-hover:text-slate-300 sm:hidden">Search...</span>
            <span className="ml-2.5 hidden flex-1 truncate text-sm text-slate-500 group-hover:text-slate-300 sm:inline">
              Search leads, deals or recordings...
            </span>
            <kbd className="ml-2 hidden items-center gap-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-1.5 py-0.5 text-[10px] font-medium text-slate-400 transition group-hover:border-white/[0.14] group-hover:text-slate-300 sm:inline-flex">
              <CommandIcon className="h-3 w-3" />K
            </kbd>
          </button>
        </div>

        {/* Quick create */}
        <DropdownMenu open={quickOpen} onOpenChange={setQuickOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "hidden h-10 items-center gap-2 rounded-xl border px-3 text-[13px] font-medium transition sm:inline-flex",
                quickOpen
                  ? "border-brand-400/40 bg-brand-500/10 text-white shadow-[0_0_24px_-6px_rgba(99,140,255,0.5)]"
                  : "border-white/[0.06] bg-white/[0.02] text-slate-300 hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white"
              )}
            >
              <Plus className={cn("h-4 w-4 transition", quickOpen && "rotate-45 text-brand-200")} />
              Quick create
              <ChevronDown className={cn("h-3.5 w-3.5 transition", quickOpen ? "rotate-180 text-brand-200" : "text-slate-500")} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={10} className={cn(panelClass, "w-[320px]")}>
            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-brand-500/20 blur-3xl" />
            <div className="relative flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="grid h-6 w-6 place-items-center rounded-md bg-brand-500/15 ring-1 ring-brand-400/25">
                  <Sparkles className="h-3 w-3 text-brand-300" />
                </div>
                <span className="text-[12.5px] font-semibold text-white">Quick create</span>
              </div>
            </div>
            <div className="relative p-1.5">
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => handleQuickAction(action.id)}
                  className="group relative flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition hover:bg-white/[0.04]"
                >
                  <div className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg ring-1 transition", action.iconBg, action.iconRing)}>
                    <action.icon className={cn("h-4 w-4", action.iconColor)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-white">{action.label}</div>
                    <div className="truncate text-[10.5px] text-slate-500">{action.hint}</div>
                  </div>
                  <ArrowUpRight className="h-3.5 w-3.5 text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-brand-200" />
                </button>
              ))}
            </div>
            <div className="relative flex items-center justify-between border-t border-white/[0.06] bg-white/[0.015] px-4 py-2 text-[10px] text-slate-500">
              <span>Press ESC to dismiss</span>
              <span className="inline-flex items-center gap-1">
                <Sparkles className="h-2.5 w-2.5 text-brand-300" />
                Fast workflows
              </span>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="ml-auto flex items-center gap-2">
          <div className="mx-1 hidden h-6 w-px bg-white/[0.06] sm:block" />

          {/* User chip */}
          <DropdownMenu open={userOpen} onOpenChange={setUserOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex h-10 items-center gap-2.5 rounded-xl border py-1 pl-1 pr-3 transition",
                  userOpen
                    ? "border-white/[0.14] bg-white/[0.05]"
                    : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.14] hover:bg-white/[0.04]"
                )}
              >
                <UserAvatar name={fullName || user?.email || "?"} url={user?.avatar} size={32} />
                <div className="hidden flex-col items-start leading-tight md:flex">
                  <span className="max-w-[140px] truncate text-[12.5px] font-semibold text-white">{fullName || user?.email}</span>
                  {roleName && <span className="text-[10.5px] text-slate-400">{roleName}</span>}
                </div>
                <ChevronDown className={cn("hidden h-3.5 w-3.5 text-slate-400 transition md:block", userOpen && "rotate-180 text-white")} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={10} className={cn(panelClass, "w-[280px]")}>
              <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-brand-500/[0.18] blur-3xl" />
              <div className="relative flex items-center gap-3 border-b border-white/[0.06] px-4 py-3.5">
                <UserAvatar name={fullName || user?.email || "?"} url={user?.avatar} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[13px] font-semibold text-white">{fullName || user?.email}</span>
                    {roleName && (
                      <span className="shrink-0 rounded bg-brand-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-200 ring-1 ring-brand-400/30">
                        {roleName}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-[10.5px] text-slate-500">{user?.email}</div>
                </div>
              </div>
              <div className="relative p-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setUserOpen(false);
                    navigate(PATH_SETTINGS_PROFILE);
                  }}
                  className="group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition hover:bg-white/[0.04]"
                >
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/[0.04] text-slate-300 ring-1 ring-white/10 transition group-hover:bg-white/[0.06] group-hover:text-white">
                    <UserIcon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-white">Profile</div>
                    <div className="truncate text-[10.5px] text-slate-500">View account & preferences</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUserOpen(false);
                    navigate(PATH_LOGOUT);
                  }}
                  className="group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition hover:bg-red-500/[0.08]"
                >
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-red-500/10 text-red-300 ring-1 ring-red-500/25 transition group-hover:bg-red-500/15">
                    <LogOut className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-red-200 group-hover:text-red-100">Log out</div>
                    <div className="truncate text-[10.5px] text-slate-500">End this session</div>
                  </div>
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} pages={searchPages} />

      {/* Quick create targets (mounted globally, page-independent) */}
      <AddLeadModal open={createOpen === "lead"} onClose={() => setCreateOpen(null)} />
      <AddDealSidebar open={createOpen === "deal"} onClose={() => setCreateOpen(null)} />
      <CreatePipelineModal open={createOpen === "pipeline"} onClose={() => setCreateOpen(null)} />
    </>
  );
};

export default MainTopBar;
